import axios from "../../../helpers/axios";
import { voiceFileName } from "./useWebVoiceRecorder";

/**
 * Задания офиса на сайте — те же маршруты и правила, что в приложении:
 *   /office/:officeId/tasks          — задания (listId, parentId, flow, pinned);
 *   /office/:officeId/task-lists     — группы: «Мои задания», «Общие задания офиса», общие группы;
 *   /office/:officeId/order-settings — «Настройки заказов» (работы по заказу);
 *   /office/:officeId/orders/:orderId/template-tasks — добавить работы к заказу.
 * Права проверяет сервер. Здесь только запросы и подписи.
 */

const enc = encodeURIComponent;
const tasksBase = (officeId) => `/office/${enc(officeId)}/tasks`;
const listsBase = (officeId) => `/office/${enc(officeId)}/task-lists`;

/** Событие для страницы заданий: «что-то поменялось — перечитай». */
export const TASKS_CHANGED_EVENT = "office-tasks:changed";

export const emitTasksChanged = (detail = {}) => {
    try {
        window.dispatchEvent(new CustomEvent(TASKS_CHANGED_EVENT, { detail }));
    } catch (_error) { }
};

export const getApiError = (error, fallback = "Не получилось. Попробуйте ещё раз.") =>
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    fallback;

/* ---------- задания ---------- */

/** listId: id группы, "personal" — мои личные, "general" — «Общие задания офиса», пусто — всё видимое. */
export const fetchTasks = async (officeId, params = {}) => {
    const res = await axios.get(tasksBase(officeId), {
        params: {
            listId: params.listId || undefined,
            parentId: params.parentId || undefined,
            orderId: params.orderId || undefined,
            productId: params.productId || undefined,
            status: params.status || undefined,
            q: params.q || undefined,
        },
    });

    return {
        tasks: Array.isArray(res.data?.tasks) ? res.data.tasks : [],
        canManage: res.data?.meta?.canManage === true,
    };
};

/** Задание с подзаданиями, группой и правами (canManage — руководит, canWork — может выполнять). */
export const fetchTask = async (officeId, taskId) => {
    const res = await axios.get(`${tasksBase(officeId)}/${enc(taskId)}`);
    return {
        task: res.data?.task || null,
        canManage: res.data?.meta?.canManage === true,
        canWork: res.data?.meta?.canWork === true,
    };
};

export const createTask = async (officeId, payload) => {
    const res = await axios.post(tasksBase(officeId), payload);
    return res.data?.task || null;
};

export const updateTask = async (officeId, taskId, patch) => {
    const res = await axios.patch(`${tasksBase(officeId)}/${enc(taskId)}`, patch);
    return res.data?.task || null;
};

export const deleteTask = async (officeId, taskId) => {
    await axios.delete(`${tasksBase(officeId)}/${enc(taskId)}`);
};

export const runTaskAction = async (officeId, taskId, action) => {
    const res = await axios.post(`${tasksBase(officeId)}/${enc(taskId)}/action`, { action });
    return res.data?.task || null;
};

export const toggleChecklist = async (officeId, taskId, itemId, completed) => {
    const res = await axios.patch(`${tasksBase(officeId)}/${enc(taskId)}/checklist/${enc(itemId)}`, { completed });
    return res.data?.task || null;
};

export const addComment = async (officeId, taskId, text) => {
    const res = await axios.post(`${tasksBase(officeId)}/${enc(taskId)}/comment`, { text });
    return res.data?.task || null;
};

export const uploadPhoto = async (officeId, taskId, file, kind = "report") => {
    const data = new FormData();
    data.append("photo", file);
    data.append("kind", kind === "reference" ? "reference" : "report");
    const res = await axios.post(`${tasksBase(officeId)}/${enc(taskId)}/photos`, data);
    return res.data?.task || null;
};

export const deletePhoto = async (officeId, taskId, photoId) => {
    const res = await axios.delete(`${tasksBase(officeId)}/${enc(taskId)}/photos/${enc(photoId)}`);
    return res.data?.task || null;
};

/** Фото и голосовые закрытые: берём файлом с токеном в заголовке (не в адресе). */
export const fetchPhotoBlob = async (officeId, taskId, photoId) => {
    const res = await axios.get(`${tasksBase(officeId)}/${enc(taskId)}/photos/${enc(photoId)}`, { responseType: "blob" });
    return res.data;
};

export const fetchVoiceBlob = async (officeId, taskId, voiceId) => {
    const res = await axios.get(`${tasksBase(officeId)}/${enc(taskId)}/voices/${enc(voiceId)}`, { responseType: "blob" });
    return res.data;
};

/** Голосовое к заданию: target "task" — описание голосом, "comment" — голосовой комментарий. */
export const uploadVoice = async (officeId, taskId, voice, target = "comment") => {
    const name = voiceFileName(voice?.mimeType || voice?.blob?.type);
    const data = new FormData();
    data.append("voice", voice.blob, name);
    data.append("target", target === "task" ? "task" : "comment");
    data.append("originalName", name);
    data.append("durationSec", String(Math.max(1, Math.round(Number(voice.durationMs || 0) / 1000))));
    const res = await axios.post(`${tasksBase(officeId)}/${enc(taskId)}/voices`, data, { timeout: 180000 });
    return res.data?.task || null;
};

export const retryVoice = async (officeId, taskId, voiceId) => {
    const res = await axios.post(`${tasksBase(officeId)}/${enc(taskId)}/voices/${enc(voiceId)}/transcribe`);
    return res.data?.task || null;
};

export const deleteVoice = async (officeId, taskId, voiceId) => {
    const res = await axios.delete(`${tasksBase(officeId)}/${enc(taskId)}/voices/${enc(voiceId)}`);
    return res.data?.task || null;
};

/* ---------- группы ---------- */

export const fetchTaskGroups = async (officeId) => {
    const res = await axios.get(listsBase(officeId));
    return {
        lists: Array.isArray(res.data?.lists) ? res.data.lists : [],
        canManage: res.data?.meta?.canManage === true,
    };
};

export const createTaskGroup = async (officeId, draft) => {
    const res = await axios.post(listsBase(officeId), draft);
    return res.data?.list || null;
};

export const updateTaskGroup = async (officeId, listId, patch) => {
    const res = await axios.patch(`${listsBase(officeId)}/${enc(listId)}`, patch);
    return res.data?.list || null;
};

export const deleteTaskGroup = async (officeId, listId) => {
    const res = await axios.delete(`${listsBase(officeId)}/${enc(listId)}`);
    return res.data?.message || "";
};

/* ---------- «Настройки заказов» ---------- */

export const TRIGGER_LABEL = {
    created: "Сразу при оформлении",
    confirmed: "Когда заказ подтверждён",
    work: "Когда заказ ушёл в производство",
};

const normalizeTemplate = (item = {}) => ({
    key: String(item.key || ""),
    title: String(item.title || ""),
    description: String(item.description || ""),
    enabled: item.enabled !== false,
    defaultOn: item.defaultOn !== false,
    trigger: ["created", "confirmed", "work"].includes(item.trigger) ? item.trigger : "created",
    listId: item.listId ? String(item.listId) : null,
    assigneeIds: Array.isArray(item.assigneeIds) ? item.assigneeIds.map(String) : [],
    dueDays: Number(item.dueDays || 0),
    flow: item.flow === "simple" ? "simple" : "review",
    priority: ["low", "normal", "high", "urgent"].includes(item.priority) ? item.priority : "normal",
    checklist: Array.isArray(item.checklist) ? item.checklist.map(String) : [],
});

const normalizeOrderSettings = (data = {}) => ({
    saved: data?.settings?.saved === true,
    templates: (Array.isArray(data?.settings?.taskTemplates) ? data.settings.taskTemplates : []).map(normalizeTemplate),
    lists: Array.isArray(data?.lists) ? data.lists : [],
    canEdit: data?.canEdit === true,
});

export const fetchOrderSettings = async (officeId) => {
    const res = await axios.get(`/office/${enc(officeId)}/order-settings`);
    return normalizeOrderSettings(res.data);
};

export const saveOrderSettings = async (officeId, templates) => {
    const res = await axios.put(`/office/${enc(officeId)}/order-settings`, { taskTemplates: templates });
    return { ...normalizeOrderSettings(res.data), message: res.data?.message || "" };
};

export const addOrderWorks = async (officeId, orderId, taskKeys) => {
    const res = await axios.post(`/office/${enc(officeId)}/orders/${enc(orderId)}/template-tasks`, { taskKeys });
    return res.data || {};
};

/* ---------- подписи и правила ---------- */

export const TASK_STATUS = {
    published: { label: "Доступно", tone: "available" },
    accepted: { label: "Принято", tone: "accepted" },
    in_progress: { label: "В работе", tone: "progress" },
    review: { label: "На проверке", tone: "review" },
    done: { label: "Готово", tone: "done" },
    rework: { label: "На доработке", tone: "rework" },
    cancelled: { label: "Отменено", tone: "cancelled" },
};

export const PRIORITY = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочно",
};

export const ACTION_LABEL = {
    claim: "Взять себе",
    accept: "Принять",
    start: "Начать",
    submit: "Отправить на проверку",
    approve: "Принять работу",
    rework: "На доработку",
    cancel: "Отменить",
    reopen: "Вернуть в работу",
    complete: "Готово",
};

export const idOf = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return String(value._id || value.id || value.userId?._id || value.userId || "");
};

export const personName = (user = {}) => (
    [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
    user?.email ||
    user?.username ||
    "Сотрудник"
);

export const isAssigned = (task, userId) => (
    !!userId && (
        idOf(task?.claimedBy) === userId ||
        (task?.assignees || []).some((user) => idOf(user) === userId)
    )
);

export const whoText = (task) => {
    if (task?.assignmentType === "board" && !task?.claimedBy) return "На доске — может взять любой";
    const people = task?.claimedBy ? [task.claimedBy] : task?.assignees || [];
    return people.map(personName).filter(Boolean).join(", ") || "Не назначено";
};

/** Какие кнопки показать. Окончательно решает сервер (403, если нельзя). Как в приложении. */
export const availableActions = (task, userId, canManage) => {
    const assigned = isAssigned(task, userId);
    const open = !["done", "cancelled"].includes(task.status);
    const list = [];

    if (task.flow === "simple") {
        if (task.assignmentType === "board" && task.status === "published" && !task.claimedBy) list.push("claim");
        if (open && (assigned || canManage)) {
            if (["published", "accepted", "rework"].includes(task.status)) list.push("start");
            list.push("complete");
        }
        if (!open && (assigned || canManage)) list.push("reopen");
        if (canManage && open) list.push("cancel");
        return list;
    }

    if (task.assignmentType === "board" && task.status === "published" && !task.claimedBy) list.push("claim");
    if (task.status === "published" && assigned) list.push("accept");
    if (["accepted", "rework"].includes(task.status) && (assigned || canManage)) list.push("start");
    if (task.status === "in_progress" && assigned) list.push("submit");
    if (task.status === "review" && canManage) list.push("approve", "rework");
    if (canManage && ["done", "cancelled"].includes(task.status)) list.push("reopen");
    if (canManage && open) list.push("cancel");
    return list;
};

/** Кружок «готово» в списке — у заданий без проверки. */
export const canToggleDone = (task, userId, canManage) => (
    task.flow === "simple" &&
    task.status !== "cancelled" &&
    (canManage || isAssigned(task, userId))
);

/* ---------- сроки ---------- */

export const DUE_GROUPS = [
    ["overdue", "Просрочено"],
    ["today", "Сегодня"],
    ["tomorrow", "Завтра"],
    ["week", "На этой неделе"],
    ["later", "Позже"],
    ["none", "Без срока"],
    ["past", "Срок прошёл (закрытые)"],
];

const FINISHED = new Set(["done", "cancelled"]);
const DAY_MS = 86400000;
const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const isOverdue = (task, now = new Date()) => {
    const due = toDate(task?.dueAt);
    return !!due && !FINISHED.has(String(task?.status || "")) && due.getTime() < now.getTime();
};

export const dueGroupOf = (task, now = new Date()) => {
    const due = toDate(task?.dueAt);
    if (!due) return "none";
    if (isOverdue(task, now)) return "overdue";
    const days = Math.round((startOfDay(due).getTime() - startOfDay(now).getTime()) / DAY_MS);
    if (days < 0) return "past";
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    if (days < 7) return "week";
    return "later";
};

export const dueText = (value) => {
    const due = toDate(value);
    if (!due) return "";
    const now = new Date();
    const days = Math.round((startOfDay(due).getTime() - startOfDay(now).getTime()) / DAY_MS);
    const endOfDay = due.getHours() === 23 && due.getMinutes() === 59;
    const time = endOfDay ? "до конца дня" : due.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    const day = days === 0
        ? "Сегодня"
        : days === 1
            ? "Завтра"
            : days === -1
                ? "Вчера"
                : due.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
    return `${day}, ${time}`;
};

export const dateTimeText = (value) => {
    const date = toDate(value);
    if (!date) return "";
    return date.toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

/** Всё задание одним текстом — скопировать в чат или заметки. */
export const buildTaskText = (task) => {
    const parts = [];
    if (task?.title) parts.push(task.title);
    if (task?.description) parts.push(task.description);
    (task?.voices || [])
        .filter((voice) => voice.target === "task" && voice.transcriptStatus === "done" && voice.transcriptText)
        .forEach((voice) => {
            const text = String(voice.transcriptText || "").trim();
            if (text && !parts.some((part) => part.includes(text))) parts.push(text);
        });
    if (task?.dueAt) parts.push(`Срок: ${dueText(task.dueAt)}`);
    if (task?.checklist?.length) {
        parts.push(["Чек-лист:", ...task.checklist.map((item) => `${item.completed ? "✓" : "•"} ${item.text}`)].join("\n"));
    }
    return parts.join("\n\n").trim();
};

/** Адрес раздела, куда ведёт связь задания (заказ, товар, склад, материал). */
export const relationSearch = (kind, task, officeId) => {
    const params = new URLSearchParams();
    params.set("office", officeId);

    if (kind === "order") {
        params.set("tab", "orders");
        params.set("taskOrder", idOf(task?.links?.order?.orderId));
    }
    if (kind === "product") {
        params.set("tab", "products");
        params.set("taskProduct", idOf(task?.links?.product?.productId));
    }
    if (kind === "warehouse") {
        params.set("tab", "warehouse");
        params.set("taskWarehouse", idOf(task?.links?.warehouse?.warehouseId));
    }
    if (kind === "material") {
        params.set("tab", "warehouse");
        if (task?.links?.warehouse?.warehouseId) params.set("taskWarehouse", idOf(task.links.warehouse.warehouseId));
        params.set("taskMaterial", idOf(task?.links?.material?.materialId));
    }

    return `?${params.toString()}`;
};

export const GROUP_COLORS = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#8b5cf6", "#64748b"];
